/*
 * kly-lightbox.js — KLY Clothing custom product-gallery lightbox
 * Port of exos-lightbox.js (owner-approved behaviour) hardened for the
 * "KLY - V4" premium theme.
 *
 * Behaviour (identical to Exos):
 *   tap/click a gallery image -> fixed dark overlay, image centred, no
 *   click-to-zoom on the image itself, native pinch allowed, prev/next arrows
 *   (hidden when there is only one image), swipe on touch, Esc / ArrowLeft /
 *   ArrowRight, close on X or backdrop, page scroll locked while open,
 *   Shopify CDN width bumped for a sharp image with no cropping.
 *
 * KLY-specific hardening (see header comments inline):
 *   - scoped to the SLIDER that was actually clicked, i.e.
 *     [data-product-single-media-slider] (2 sections can put two galleries on
 *     one page: sections/product.liquid + section-product.liquid). NOTE: the
 *     enclosing [data-product-single-media-group] is deliberately NOT used as
 *     the scope — it also contains the thumbnail strip, whose anchors are raw
 *     links to naked CDN JPEGs that the theme owns and we must not touch.
 *   - drag-vs-tap guard so a Flickity carousel SWIPE never opens the overlay
 *     (Flickity only calls preventDefault(), never stopPropagation(), so a
 *     naive capture-phase listener fires on every swipe). The guard tracks the
 *     gesture PATH (a latched `moved` flag), never the press duration: a
 *     stationary press of any length is a tap by Flickity's own definition
 *     (dragThreshold 3px, no duration component) and must always open.
 *   - never uses .is-moving as the sole guard (that class is only cleared by
 *     Flickity's `settle` event and can get permanently stuck)
 *   - currentSrc || src, because every non-featured slide is loading="lazy"
 *     (and on the homepage ALL slides are lazy) so currentSrc is "" until
 *     visited — hence load-before-swap plus a spinner in show()
 *   - image list scoped to the slider so the thumbnail strip, collection-grid
 *     <figure data-product-image> and <sibling-swatches data-product-image>
 *     are never collected
 *   - image slides only (data-type="image"); video / external_video / model
 *     slides keep their own deferred-media poster behaviour
 *   - skips the quick-view popup entirely (that is a PhotoSwipe modal that
 *     reuses the same .product-gallery__media-slide markup)
 *   - keyboard route (tabindex/role/aria-label + Enter/Space), because the
 *     enable_zoom flip removes the homepage's only focusable image anchor
 *   - z-index above announcement bar (135), sticky header (9000), cart bar
 *     (2001), cart drawer (9999), .pswp (10000)
 *   - page scroll is locked exactly the way Exos did it: documentElement
 *     overflow only. The theme's own theme:scroll:lock contract is NOT used —
 *     it installs a non-passive document touchmove handler that preventDefaults
 *     every single-finger move inside the overlay, which kills pan-while-zoomed.
 */
(function () {
  "use strict";

  if (window.__klyLB) return;
  window.__klyLB = 1;

  var SLIDER_SEL = "[data-product-single-media-slider]";
  var SLIDE_SEL = ".product-gallery__media-slide";
  var IMG_SEL = "img[data-product-image]";
  /* Quick view is a PhotoSwipe modal that renders the very same gallery
     markup. Never open on top of it.
     `.pswp` is the PRIMARY guard and the one that actually matches (the popup
     is hosted on the .pswp element, 2 per page on every live template).
     `.popup-quick-view` is the runtime class the theme adds to that same
     element. `[data-quick-view-inner]` is the real inner container. Do not
     remove `.pswp` — the other two are belt and braces. */
  var EXCLUDE_SEL = ".pswp, .popup-quick-view, [data-quick-view-inner]";

  /* Slides that get the keyboard/pointer affordance. */
  var ELIGIBLE_SEL = SLIDE_SEL + '[data-type="image"] ' + IMG_SEL;

  /* Bumped width for the overlay image. 1960 is the top rung of the theme's
     own srcset ladder (snippets/media.liquid image_widths), so on desktop the
     browser has often already cached this exact width. */
  var BIG_W = 1960;

  var CURSOR_SEL = SLIDER_SEL + " " + ELIGIBLE_SEL;

  var css =
    /* backdrop opacity is Exos's .9 exactly — owner-approved chrome */
    ".kly-lb{position:fixed;top:0;right:0;bottom:0;left:0;z-index:100001;display:none;" +
    "align-items:center;justify-content:center;background:rgba(0,0,0,.9);padding:16px;" +
    "-webkit-tap-highlight-color:transparent;}" +
    ".kly-lb.is-open{display:flex;}" +
    /* Container-relative, NOT 92vw/92vh: iOS resolves vh against the LARGE
       viewport, so in landscape (toolbar visible) a 92vh image is taller than
       the visible area and gets clipped top and bottom, with minimum-scale=1.0
       in head.liquid blocking any pinch-out recovery. The flex parent's 16px
       padding does the insetting instead. */
    ".kly-lb__img{max-width:100%;max-height:100%;min-width:0;min-height:0;" +
    "width:auto;height:auto;object-fit:contain;" +
    "user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;}" +
    /* no font-family override — the ×/‹/› glyphs render in the system UI face,
       exactly as on the Exos build the owner signed off on */
    ".kly-lb__btn{position:fixed;background:rgba(0,0,0,.35);border:0;color:#fff;cursor:pointer;" +
    "z-index:100002;line-height:1;}" +
    ".kly-lb__btn:hover{background:rgba(0,0,0,.6);}" +
    ".kly-lb__close{top:12px;right:16px;font-size:32px;padding:2px 12px;border-radius:6px;}" +
    ".kly-lb__nav{top:50%;transform:translateY(-50%);font-size:30px;padding:8px 16px;border-radius:8px;}" +
    ".kly-lb__prev{left:10px;}" +
    ".kly-lb__next{right:10px;}" +
    ".kly-lb--single .kly-lb__nav{display:none;}" +
    /* spinner while a not-yet-downloaded slide is in flight (every non-visited
       slide is loading="lazy" and has never been fetched by the carousel) */
    ".kly-lb__spin{position:absolute;top:50%;left:50%;width:34px;height:34px;margin:-17px 0 0 -17px;" +
    "border:3px solid rgba(255,255,255,.25);border-top-color:#fff;border-radius:50%;" +
    "display:none;pointer-events:none;z-index:100002;}" +
    /* own namespace, NOT the theme's `.is-loading`: theme.css ships two
       UNQUALIFIED descendant rules (`.is-loading .loader`,
       `.is-loading .cart__item__quantity`) that would apply to anything we
       ever nest inside the overlay. */
    ".kly-lb.kly-lb--loading .kly-lb__spin{display:block;animation:kly-lb-spin .8s linear infinite;}" +
    "@keyframes kly-lb-spin{to{transform:rotate(360deg);}}" +
    CURSOR_SEL + "{cursor:zoom-in;}" +
    /* ...but never inside the quick-view modal, where we deliberately refuse to
       open. Advertising a zoom cursor on a dead target is the exact
       "looks clickable, does nothing" symptom this change exists to remove. */
    ".pswp " + CURSOR_SEL + ",.popup-quick-view " + CURSOR_SEL + "{cursor:default;}";

  /* ---------- tiny helpers (ES5-safe) ---------- */

  function matches(el, sel) {
    if (!el || el.nodeType !== 1) return false;
    var fn =
      el.matches ||
      el.msMatchesSelector ||
      el.webkitMatchesSelector ||
      el.mozMatchesSelector;
    return fn ? fn.call(el, sel) : false;
  }

  function closest(el, sel) {
    if (el && el.closest) {
      try { return el.closest(sel); } catch (err) { /* fall through */ }
    }
    var node = el;
    while (node && node.nodeType === 1) {
      if (matches(node, sel)) return node;
      node = node.parentElement || node.parentNode;
    }
    return null;
  }

  /* Build a clean, proportional Shopify CDN URL (width ONLY — strips
     crop/height so landscape/portrait images are never cropped). The theme's
     own src carries `crop=center&height=NNNN&width=NNNN`, which WOULD crop. */
  function sized(rawSrc, w) {
    if (!rawSrc) return rawSrc;
    var base = rawSrc.split("?")[0];
    var qi = rawSrc.indexOf("?");
    var q = qi >= 0 ? rawSrc.slice(qi + 1) : "";
    var m = q.match(/(?:^|&)v=([^&]*)/);
    return base + "?width=" + w + (m ? "&v=" + m[1] : "");
  }

  /* ---------- thumbnail raw-link neutraliser ----------
     snippets/media-thumb.liquid renders every PDP / homepage gallery thumbnail
     as a REAL anchor to a naked CDN JPEG:

       <a href="{{ thumbnail | img_url: '2048x' }}" class="product-single__thumbnail-link">

     Nothing in the markup stops that navigation. The only suppression is bound
     at RUNTIME by product-info.js, inside initProductSlider(), and it is triply
     gated: it needs checkSlider() to pick the init branch (image_layout ==
     'carousel', or a viewport under theme.sizes.large), it needs
     `i.length > 1 && (this.flkty = new Flickity(...))` to be truthy, and it
     needs the `null !== e` [data-product-single-media-thumbs] branch. If any
     gate fails the anchors stay navigable FOREVER, and a tap dumps the shopper
     onto a bare 2048px JPEG on a blank white page.

     This listener is the ungated backstop. Registered at script scope, before
     build(), so it exists the instant this file executes rather than waiting on
     DOMContentLoaded.

     HONEST SCOPE — this handler closes almost nothing on its own.
     This asset is itself `defer`, and in snippets/product.liquid it is emitted
     AFTER product-info.js. Deferred scripts execute in document order, and
     product-info.js binds its own preventDefault synchronously inside
     connectedCallback -> productSlider() -> checkSlider(). So the theme's
     binding always lands FIRST, unconditionally, on every page: this listener
     shaves exactly 0ms off the pre-boot window.

     It does NOT close the image_layout-off-'carousel' case either, though an
     earlier draft of this comment claimed it did. In that configuration the
     wrapper is .product-single__wrapper--stacked / --grid, and theme.css hides
     the strip outright (`.product-single__wrapper--stacked
     .product-single__thumbnails, .product-single__wrapper--grid
     .product-single__thumbnails{display:none}`) — so there is no visible tap
     target in precisely the state where the theme's binding is skipped.

     What is left is genuinely narrow: a Flickity constructor that throws or
     returns falsy, or product-info.js alone failing to execute while this file
     still does. Keep it as cheap insurance — in particular, a future THEME
     UPDATE would overwrite media-thumb.liquid and restore the raw href, and
     this guard is what catches that regression.

     THE REAL FIX IS IN LIQUID and has been applied: media-thumb.liquid no
     longer emits an href, so an <a> with no href has no default action for any
     input method and the race window does not exist. The hasAttribute("href")
     guard below means this handler is a self-disabling no-op today.

     WHY PURE preventDefault AND NOTHING MORE.
     The tempting "improvement" is to make a pre-boot tap actually do something
     — switch the main image ourselves, or open the lightbox on it. Both are
     worse:
       - Switching the main image means replicating switchMedia() plus the
         media--hidden bookkeeping against a gallery Flickity has not
         transformed yet. When Flickity does boot it selects cell 0
         unconditionally, silently undoing our swap: the shopper watches the
         photo change and then change back. That converts a cosmetic bug into a
         visible one.
       - Opening the lightbox is forbidden anyway (the theme owns this click
         post-boot, and it must keep owning it), and it would mean the same tap
         behaves differently before and after boot. It also buys nothing on
         mobile, where theme.css hides every thumbnail but the first until
         .flickity-enabled appears — and that first thumbnail is the photo
         already on screen.
     Doing nothing for a sub-second window is strictly better than navigating
     off-site, and it matches what the theme itself does post-boot when the
     tapped thumbnail is already the selected cell.

     WHY preventDefault ONLY, NEVER stopPropagation.
     Post-boot, Flickity's asNavFor drives the main gallery from this cell, so
     killing propagation would break image switching outright. It is also
     provably unnecessary: asNavFor fires on `staticClick`, which Unidragger
     emits from _dragPointerUp -> _staticClick, i.e. off POINTERUP. By the time
     this `click` listener runs, staticClick has already been dispatched and the
     main image has already switched. Suppressing the click's default action
     cannot reach it.

     Suppression is UNCONDITIONAL — no modifier-key escape hatch. The theme's
     own binding preventDefaults every click regardless of ctrl/cmd/shift, and
     matching it exactly is the point: behaviour must be identical before and
     after boot. "Open link in new tab" on a thumbnail would only ever yield a
     naked JPEG, which is the hazard, not a feature.

     Not scoped away from the quick-view modal either. Unlike opening the
     lightbox, refusing a navigation is safe everywhere, and the same raw href
     would be the same hazard there. (Quick view sets hide_product_thumbnails =
     true today, so this is belt and braces.)

     Guarded on hasAttribute("href") so that if media-thumb.liquid is later
     fixed properly — href dropped, or the anchor swapped for a <button> — this
     handler degrades to a no-op instead of preventDefaulting a real control. */

  var THUMB_LINK_SEL = ".product-single__thumbnail-link";

  document.addEventListener(
    "click",
    function (e) {
      var a = closest(e.target, THUMB_LINK_SEL);
      if (!a) return;
      if (!a.hasAttribute || !a.hasAttribute("href")) return;
      e.preventDefault();
      /* deliberately NO stopPropagation / stopImmediatePropagation — see above */
    },
    true
  );

  function build() {
    if (document.querySelector(".kly-lb")) return;

    var st = document.createElement("style");
    st.setAttribute("data-kly-lb", "");
    st.appendChild(document.createTextNode(css));
    document.head.appendChild(st);

    var ov = document.createElement("div");
    ov.className = "kly-lb";
    ov.setAttribute("role", "dialog");
    ov.setAttribute("aria-modal", "true");
    ov.setAttribute("aria-hidden", "true");
    /* harmless; kept so that if anything else in the theme ever runs
       scroll-lock while we are open, the overlay is at least whitelisted. */
    ov.setAttribute("data-scroll-lock-scrollable", "");
    ov.innerHTML =
      '<button type="button" class="kly-lb__btn kly-lb__close" aria-label="Close">&times;</button>' +
      '<button type="button" class="kly-lb__btn kly-lb__nav kly-lb__prev" aria-label="Previous image">&#8249;</button>' +
      '<button type="button" class="kly-lb__btn kly-lb__nav kly-lb__next" aria-label="Next image">&#8250;</button>' +
      '<div class="kly-lb__spin" aria-hidden="true"></div>' +
      '<img class="kly-lb__img" alt="">';
    document.body.appendChild(ov);

    var img = ov.querySelector(".kly-lb__img");
    var btnClose = ov.querySelector(".kly-lb__close");
    var items = [];
    var idx = 0;
    var htmlOverflowWas = "";
    var locked = false;

    /* ---------- keyboard affordance ----------
       The enable_zoom flip deletes the homepage's <a class=
       "product-gallery__media-link" data-zoom-wrapper> overlays, which were the
       only focusable, Enter-activatable route to a product image. Without this
       pass the replacement would be pointer-only — a WCAG 2.1.1 regression on
       the exact surface being changed. Marking the <img> focusable also makes
       it a legitimate focus-return target (see openFrom). */
    function markEligible(root) {
      var scope = root && root.querySelectorAll ? root : document;
      var sliders = scope.querySelectorAll(SLIDER_SEL);
      for (var s = 0; s < sliders.length; s++) {
        var slider = sliders[s];
        if (closest(slider, EXCLUDE_SEL)) continue; // quick-view modal: hands off
        var imgs = slider.querySelectorAll(ELIGIBLE_SEL);
        for (var i = 0; i < imgs.length; i++) {
          var im = imgs[i];
          /* If the theme's own focusable zoom anchor is still in this slide
             (enable_zoom true — the homepage today, or any partial deploy),
             marking the <img> too would give ONE photo TWO tab stops. Skip it:
             the anchor already carries the keyboard route, and the click
             handler's `e.detail !== 0` guard is what lets its Enter-synthesised
             click through to openFrom(). Checked BEFORE the key attribute so a
             later deploy that removes the anchor re-marks the image. */
          var media = closest(im, ".product-gallery__media");
          if (media && media.querySelector("[data-zoom-wrapper]")) continue;
          if (im.getAttribute("data-kly-lb-key") === "1") continue;
          im.setAttribute("data-kly-lb-key", "1");
          if (!im.hasAttribute("tabindex")) im.setAttribute("tabindex", "0");
          if (!im.hasAttribute("role")) im.setAttribute("role", "button");
          if (!im.hasAttribute("aria-label")) {
            var alt = im.getAttribute("alt");
            im.setAttribute(
              "aria-label",
              alt ? alt + ", open larger image" : "Open larger image"
            );
          }
        }
      }
    }

    /* ---------- collection ---------- */

    function keyOf(el) {
      return (
        el.getAttribute("data-image-id") ||
        el.currentSrc ||
        el.getAttribute("src") ||
        el.src
      );
    }

    /* Scoped to the slider that was actually clicked. The Exos build used a
       global document.querySelector, which breaks the moment a page carries
       two galleries (homepage featured-product + any future product section)
       or when the quick-view popup injects a third. */
    function collect(slider) {
      items = [];
      if (!slider) return;

      var slides = slider.querySelectorAll(SLIDE_SEL);
      var seen = {};

      for (var s = 0; s < slides.length; s++) {
        var slide = slides[s];
        var type = slide.getAttribute("data-type");
        /* image slides only. video / external_video / model keep their own
           deferred-media poster button. (The onboarding placeholder slide has
           no data-type and no img[data-product-image], so it drops out here.)
           Strict equality, matching CURSOR_SEL / ELIGIBLE_SEL, which both hard
           require [data-type="image"]: a slide with NO data-type must be
           treated the same way everywhere. */
        if (type !== "image") continue;

        var im = slide.querySelector(IMG_SEL);
        if (!im) continue;

        var id = keyOf(im);
        if (!id || seen[id]) continue;
        seen[id] = 1;

        /* currentSrc is EMPTY on any slide the shopper has not swiped to yet:
           every non-featured slide is loading="lazy", and on the homepage the
           featured slide is lazy too (media.liquid only sets eager when
           template.name == 'product'). Always fall back to src. */
        var raw = im.getAttribute("src") || im.currentSrc || "";

        items.push({
          id: id,
          /* shown = whatever the browser already decoded (instant, cached).
             May legitimately be "" for an unvisited lazy slide. */
          shown: im.currentSrc || "",
          /* big = clean proportional full-width, never cropped. */
          big: sized(raw, BIG_W),
          alt: im.alt || ""
        });
      }
    }

    /* ---------- display ---------- */

    function preload(i) {
      if (items.length < 2) return;
      var it = items[(i + items.length) % items.length];
      if (it && it.big) new Image().src = it.big;
    }

    function setLoading(on) {
      if (on) ov.classList.add("kly-lb--loading");
      else ov.classList.remove("kly-lb--loading");
    }

    /* Every slide except the one the shopper was looking at is loading="lazy"
       and has NEVER been fetched (the Flickity viewport is overflow:hidden, so
       off-screen cells never intersect). Assigning img.src to an undownloaded
       URL drops the <img> to zero intrinsic size — the outgoing photo vanishes
       and the overlay is solid black for the length of a 3000x3000 download.
       So: load into a detached Image() first, keep the current photo on screen
       with a spinner, and only swap when the bytes are actually there. */
    var loadToken = 0;
    /* The in-flight preloader is parked here so it cannot be collected while
       its request is outstanding (nothing else references it: `pre` holds the
       handlers, not the other way round). */
    var keepAlive = null;
    var STALL_MS = 8000;

    function show(i) {
      if (!items.length) return;
      idx = (i + items.length) % items.length;
      var it = items[idx];

      /* Emptiness is checked BEFORE the token bump. Bumping first would cancel
         an in-flight load whose onload/onerror then bails on the token check
         without ever clearing the spinner — leaving it turning forever. */
      if (!it.shown && !it.big) { setLoading(false); return; }

      var token = ++loadToken;

      /* +/-2, not +/-1: two quick taps on Next outrun a +/-1 window entirely.
         But the far pair is DEFERRED until this slide settles — firing four
         3000x3000 fetches alongside the one the shopper is actually waiting on
         makes THIS spinner last longer. Latched so it runs at most once. */
      var didFar = false;
      function farPreload() {
        if (didFar) return;
        didFar = true;
        preload(idx + 2);
        preload(idx - 2);
      }

      if (it.shown) {
        /* already decoded by this browser: painting it is instant, no blank
           frame — the Exos "instant then upgrade" path. Clear the spinner: a
           previous slide may have left it running, and it would now be turning
           on top of a fully visible photo. */
        img.alt = it.alt;
        img.src = it.shown;
        setLoading(false);
      } else {
        /* nothing cached: hold the outgoing photo and show the spinner. */
        setLoading(true);
      }

      if (it.big && it.big !== it.shown) {
        /* NOTE: this upgrade path never calls setLoading(true). Once anything
           is on screen the spinner must not be re-armed. */
        var timer = null;
        var pre = new Image();
        keepAlive = pre;
        pre.onload = function () {
          if (timer) { clearTimeout(timer); timer = null; }
          if (token !== loadToken) return; // superseded by a later show()
          img.alt = it.alt;
          img.src = it.big;
          setLoading(false);
          farPreload();
        };
        pre.onerror = function () {
          if (timer) { clearTimeout(timer); timer = null; }
          if (token !== loadToken) return;
          setLoading(false);
          /* last resort: let the <img> try the URL itself rather than leaving
             the shopper on the previous photo forever. */
          if (!it.shown) { img.alt = it.alt; img.src = it.big; }
          farPreload();
        };
        /* an aborted request fires neither onload nor onerror on some engines */
        pre.onabort = pre.onerror;
        /* ...and a request that simply STALLS fires nothing at all, which would
           leave the spinner running for the rest of the session. */
        timer = setTimeout(function () {
          timer = null;
          if (token !== loadToken) return;
          setLoading(false);
          if (!it.shown) { img.alt = it.alt; img.src = it.big; }
          farPreload();
        }, STALL_MS);
        pre.src = it.big;
      } else {
        setLoading(false);
        setTimeout(farPreload, 0);
      }

      preload(idx + 1);
      preload(idx - 1);
    }

    function next() { show(idx + 1); }
    function prev() { show(idx - 1); }

    /* ---------- scroll lock ----------
       Exactly the Exos mechanism: a style change on <html>. Deliberately NOT
       the theme's theme:scroll:lock contract — scroll-lock (vendor.js) installs
       a non-passive document touchmove handler that reaches
       `t.cancelable && t.preventDefault()` for every SINGLE-finger move inside
       the overlay (traced: the overlay has no scrollable overflow, so u()/p()
       both return true and c() finds no scrollable ancestor). That leaves pinch
       working but kills pan-while-zoomed — a capability Exos had. The
       data-scroll-lock-scrollable attribute provably does not prevent it, and
       neither does removing it (the else arm preventDefaults too). */

    function lockScroll() {
      /* Idempotent. A second openFrom() while already open would otherwise
         re-snapshot the overflow as "hidden", and close() would then restore
         "hidden" — leaving the shopper on a permanently unscrollable page with
         no overlay in sight and no way back. */
      if (!locked) {
        htmlOverflowWas = document.documentElement.style.overflow;
        locked = true;
      }
      document.documentElement.style.overflow = "hidden";

      /* theme.js installs `document.ontouchmove = e => e.preventDefault()` for
         the whole duration of a Flickity drag and only restores it on dragEnd.
         If that hand-off was missed, pinch/pan inside the overlay would be
         dead. Clear it defensively; the theme reassigns on its next dragEnd. */
      document.ontouchmove = null;
    }

    function unlockScroll() {
      if (locked) {
        document.documentElement.style.overflow = htmlOverflowWas;
        htmlOverflowWas = "";
        locked = false;
      }
      document.ontouchmove = null;
    }

    /* ---------- open / close ---------- */

    var openedAt = 0;
    var pushedState = false;
    var triggerWas = null;
    var haveTriggerWas = false;
    var downOnBackdrop = null; // null = unknown -> fail open

    function openFrom(el, slider) {
      collect(slider);
      if (!items.length) return;

      /* classList.toggle's 2nd argument is unreliable on older engines */
      if (items.length <= 1) ov.classList.add("kly-lb--single");
      else ov.classList.remove("kly-lb--single");

      var k = keyOf(el);
      var start = 0;
      for (var i = 0; i < items.length; i++) {
        if (items[i].id === k) { start = i; break; }
      }
      show(start);

      ov.classList.add("is-open");
      ov.setAttribute("aria-hidden", "false");
      openedAt = Date.now();
      downOnBackdrop = null;
      lockScroll();

      /* Android Back / back-swipe is the universal dismiss gesture for a
         full-screen overlay. Without this the browser leaves the PDP entirely,
         and a bfcache return lands on a still-open black overlay. */
      try {
        if (!pushedState && window.history && window.history.pushState) {
          window.history.pushState({ klyLB: 1 }, "", window.location.href);
          pushedState = true;
        }
      } catch (err) { /* ignore */ }

      /* Return focus to the tapped image on close, and keep tabbing inside.
         window.theme.a11y.state.trigger is a SHARED global that other theme
         components (cart drawer) read on their own close — snapshot it and put
         it back rather than leaving ours behind to poison them. */
      try {
        if (window.theme && window.theme.a11y && window.theme.a11y.state) {
          /* only snapshot on the FIRST open, so a re-entrant open can never
             overwrite the saved value with our own <img> */
          if (!haveTriggerWas) {
            triggerWas = window.theme.a11y.state.trigger;
            haveTriggerWas = true;
          }
          window.theme.a11y.state.trigger = el;
          window.theme.a11y.trapFocus({ container: ov, elementToFocus: btnClose });
        } else {
          btnClose.focus();
        }
      } catch (err) { /* ignore */ }
    }

    function close() {
      if (!ov.classList.contains("is-open")) return;
      ov.classList.remove("is-open");
      ov.setAttribute("aria-hidden", "true");
      setLoading(false);
      loadToken++; // cancel any in-flight swap
      unlockScroll();
      img.removeAttribute("src");
      try {
        if (window.theme && window.theme.a11y) {
          window.theme.a11y.removeTrapFocus({ container: ov });
        }
      } catch (err) { /* ignore */ }
      /* restore the shared global exactly as we found it */
      if (haveTriggerWas) {
        try {
          if (window.theme && window.theme.a11y && window.theme.a11y.state) {
            window.theme.a11y.state.trigger = triggerWas;
          }
        } catch (err) { /* ignore */ }
        triggerWas = null;
        haveTriggerWas = false;
      }
      /* Pop our own history entry back off. Cleared BEFORE history.back() so
         the resulting popstate cannot recurse. */
      if (pushedState) {
        pushedState = false;
        try { window.history.back(); } catch (err) { /* ignore */ }
      }
    }

    /* ---------- drag vs tap guard ----------
       Flickity runs on this gallery at EVERY viewport width (data-tall-layout
       is "false" whenever image_layout == "carousel"), and its click
       suppression calls preventDefault() ONLY — never stopPropagation() — so a
       capture-phase document listener still fires after a swipe/mouse-drag,
       and e.defaultPrevented is still false at capture time.
       We therefore track the pointer ourselves.

       TWO deliberate properties:
       (1) NO duration component. Flickity's own detection is purely distance
           based (dragThreshold 3, X only) and a stationary press — however long
           — is routed to _staticClick, i.e. the theme calls it a click. A time
           cap only ever produces FALSE NEGATIVES: a deliberate press-and-hold,
           or an ordinary tap on a phone whose main thread stalled between
           pointerdown and click, gets silently discarded and the shopper sees
           "nothing happens" — the exact symptom this change exists to remove.
       (2) PATH tracking, not endpoint distance. `moved` latches on any
           pointermove/touchmove past the threshold and is reset on every
           pointerdown, so a rubber-band swipe (drag out, change your mind, come
           back to the start, release) is correctly read as a drag. Unlike the
           theme's own `is-moving` class this cannot get permanently stuck,
           because the reset is on pointerdown rather than on Flickity's
           `settle` event. */

    var downX = null, downY = null, moved = false;
    var MOVE_TOL = 10;   // Flickity's own drag threshold is 3px

    function markDown(x, y) {
      downX = x; downY = y; moved = false;
    }

    function markMove(x, y) {
      if (downX === null || moved) return;
      var dx = x - downX;
      var dy = y - downY;
      if (dx * dx + dy * dy > MOVE_TOL * MOVE_TOL) moved = true;
    }

    if (window.PointerEvent) {
      document.addEventListener(
        "pointerdown",
        function (e) { markDown(e.clientX, e.clientY); },
        true
      );
      document.addEventListener(
        "pointermove",
        function (e) { markMove(e.clientX, e.clientY); },
        true
      );
    } else {
      document.addEventListener(
        "touchstart",
        function (e) {
          if (e.touches && e.touches.length === 1) {
            markDown(e.touches[0].clientX, e.touches[0].clientY);
          } else {
            downX = null;
          }
        },
        true
      );
      document.addEventListener(
        "touchmove",
        function (e) {
          if (e.touches && e.touches.length === 1) {
            markMove(e.touches[0].clientX, e.touches[0].clientY);
          } else {
            moved = true; // multi-touch is never a tap
          }
        },
        true
      );
      document.addEventListener(
        "mousedown",
        function (e) { markDown(e.clientX, e.clientY); },
        true
      );
      document.addEventListener(
        "mousemove",
        function (e) { markMove(e.clientX, e.clientY); },
        true
      );
    }

    function wasDrag(e) {
      if (downX === null || downY === null) return false; // no pointer info
      if (moved) return true;                             // path says drag
      var dx = e.clientX - downX;
      var dy = e.clientY - downY;
      return dx * dx + dy * dy > MOVE_TOL * MOVE_TOL;
    }

    /* Resolve a click/keydown target to {slider, slide, img}. Returns null if
       this target is none of our business. */
    function resolve(target) {
      if (!target || target.nodeType !== 1) return null;

      /* Never fire inside the PhotoSwipe quick-view popup, which reuses the
         exact same gallery markup. */
      if (closest(target, EXCLUDE_SEL)) return null;

      var slider = closest(target, SLIDER_SEL);
      if (!slider) return null;

      var slide = closest(target, SLIDE_SEL);
      if (!slide) return null;

      var type = slide.getAttribute("data-type");
      /* strict, to match CURSOR_SEL / ELIGIBLE_SEL and collect() */
      if (type !== "image") return null; // video / model slides: hands off

      var im = slide.querySelector(IMG_SEL);
      if (!im) return null;

      return { slider: slider, slide: slide, img: im };
    }

    /* ---------- the gallery click (capture phase, slide-wrapper resolution) ----------
       Capture phase + resolving the <img> via the slide wrapper is the Exos
       technique and is still required: the slider's click target is a wrapper
       (.product-gallery__media-slide / .product-gallery__media--image /
       <figure class="lazy-image">), not the <img>. */
    document.addEventListener(
      "click",
      function (e) {
        var r = resolve(e.target);
        if (!r) return;

        /* If the theme's PhotoSwipe zoom overlay is still present (section
           setting enable_zoom == true, e.g. the homepage featured-product, or
           any future section — the schema default is TRUE), take the click
           over completely instead of stacking two lightboxes. zoom.js binds in
           BUBBLE phase on this anchor, so stopImmediatePropagation() from our
           capture-phase listener keeps PhotoSwipe from ever opening.

           This suppression is UNCONDITIONAL and runs BEFORE the drag guard: a
           swipe must open nothing at all, not fall through to the theme's
           clunky PhotoSwipe. (zoom.js's own guard is the `is-moving` class,
           which a stationary press never sets — so without hoisting this above
           wasDrag(), a slow press would still reach PhotoSwipe.)
           enable_zoom:false remains the right setting (it stops zoom.js and
           photoswipe.js downloading at all); this is the safety net for a
           stale/partial deploy or a later theme-editor re-enable. */
        var zoomAnchor = closest(e.target, "[data-zoom-wrapper], .product-gallery__media-link");
        if (zoomAnchor) {
          e.preventDefault();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        }

        /* Let every other real control inside a slide keep working. */
        if (!zoomAnchor && closest(e.target, "a[href], button, [data-deferred-media-button]")) return;

        /* detail === 0 means there was no pointing device behind this click:
           a keyboard Enter on the zoom anchor, or an el.click(). Such an event
           carries no geometry, so wasDrag() would judge it against whatever
           stale downX/downY an earlier, unrelated mouse press left behind and
           silently swallow it — leaving the homepage zoom anchor completely
           dead for keyboard users during the pre-flip window.

           The bypass is deliberately NARROW: detail === 0 alone is not enough.
           Mouse and touch-derived clicks are SUPPOSED to report detail >= 1,
           but some Android WebViews emit synthetic touch clicks with detail 0,
           and there the bare check would disable the swipe guard and let a
           carousel swipe open the overlay — a brand-new glitch, the one
           outcome this whole change exists to prevent. A genuine keyboard
           activation always has the activated element focused, so require that
           too. Fails CLOSED (guard still runs) whenever we are unsure. */
        var keyActivated = e.detail === 0 && document.activeElement === e.target;
        if (!keyActivated && wasDrag(e)) return; // carousel swipe / mouse drag

        e.preventDefault();
        openFrom(r.img, r.slider);
      },
      true
    );

    /* ---------- the gallery keydown (Enter / Space) ----------
       Bypasses wasDrag() entirely: a keyboard-synthesised activation has no
       pointer geometry (clientX/clientY are 0), so running it through the drag
       guard would let a stale downX from any earlier press swallow it. */
    document.addEventListener(
      "keydown",
      function (e) {
        if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        var target = e.target;
        if (!matches(target, ELIGIBLE_SEL)) return;
        var r = resolve(target);
        if (!r) return;
        e.preventDefault();
        openFrom(r.img, r.slider);
      },
      true
    );

    /* ---------- overlay controls ---------- */

    /* Latch where the press STARTED so a drag that begins on the photo and
       ends on the backdrop does not close. Unknown (null) fails OPEN. */
    function markBackdropDown(e) { downOnBackdrop = e.target === ov; }
    ov.addEventListener("pointerdown", markBackdropDown, true);
    ov.addEventListener("mousedown", markBackdropDown, true);
    ov.addEventListener("touchstart", markBackdropDown, true);

    ov.addEventListener("click", function (e) {
      if (e.target !== ov) return;
      var startedOnBackdrop = downOnBackdrop;
      downOnBackdrop = null;
      if (startedOnBackdrop === false) return; // gesture began on the photo
      /* Double-tap ("zoom this", or an impatient second tap) fires click #2
         against the overlay click #1 synchronously created. Without this the
         screen flashes black and closes ~30ms after opening. */
      if (Date.now() - openedAt < 350) return;
      close();
    });
    btnClose.addEventListener("click", close);
    ov.querySelector(".kly-lb__prev").addEventListener("click", function (e) {
      e.stopPropagation(); prev();
    });
    ov.querySelector(".kly-lb__next").addEventListener("click", function (e) {
      e.stopPropagation(); next();
    });

    document.addEventListener("keydown", function (e) {
      if (!ov.classList.contains("is-open")) return;
      var k = e.key;
      if (k === "Escape" || k === "Esc") { e.preventDefault(); close(); }
      else if (k === "ArrowRight") { e.preventDefault(); next(); }
      else if (k === "ArrowLeft") { e.preventDefault(); prev(); }
    });

    /* ---------- swipe inside the overlay (never hijacks a pinch or a pan) ---------- */

    var tx = null, ty = null, pinched = false;

    ov.addEventListener(
      "touchstart",
      function (e) {
        if (e.touches.length === 1) {
          tx = e.touches[0].clientX;
          ty = e.touches[0].clientY;
          pinched = false;
        } else {
          tx = null; ty = null; pinched = true;
        }
      },
      { passive: true }
    );

    ov.addEventListener(
      "touchmove",
      function (e) {
        if (e.touches.length > 1) pinched = true; // native pinch-zoom in flight
      },
      { passive: true }
    );

    ov.addEventListener(
      "touchend",
      function (e) {
        /* `pinched` only covers the pinch gesture ITSELF. Once the shopper
           lifts to one finger to pan around a zoomed photo, the next
           touchstart resets it — so consult the ACTUAL page zoom, otherwise
           every attempt to look around a zoomed garment pages the gallery
           (and throws the zoom away with it). */
        if (window.visualViewport && window.visualViewport.scale > 1.01) {
          tx = null; ty = null; return;
        }
        if (tx === null || pinched) { tx = null; ty = null; return; }
        var dx = e.changedTouches[0].clientX - tx;
        var dy = e.changedTouches[0].clientY - ty;
        tx = null; ty = null;
        if (items.length < 2) return;
        /* horizontal intent only, so a pan is not a swipe */
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          if (dx < 0) next();
          else prev();
        }
      },
      { passive: true }
    );

    /* ---------- browser history / bfcache ---------- */

    window.addEventListener("popstate", function () {
      if (!ov.classList.contains("is-open")) return;
      /* close() -> history.back() dispatches popstate ASYNCHRONOUSLY. If the
         shopper reopens before that event drains, the queued popstate would
         close the overlay they just opened (a black flash). Reuse the backdrop
         guard's just-opened window: within it, any popstate is by definition
         the stale one, because the open itself was the shopper's most recent
         action. Terminates trivially — it is a pure early return, and the
         entry pushed by that reopen is still live with pushedState === true,
         so the state stays consistent.

         NOT gated on e.state: after history.back() the event fires with the
         state of the entry we land ON, which is the pre-lightbox entry (null)
         in BOTH the genuine Android-Back case and the stale-reopen case. The
         value is identical either way, so it carries no signal to filter on —
         gating on it would only break Android Back entirely. */
      if (Date.now() - openedAt < 350) return;
      pushedState = false; // the entry is already gone; do not call back()
      close();
    });

    /* A bfcache restore must never come back to a still-open black overlay
       over a page that scrolls underneath it. close() unlocks internally. */
    window.addEventListener("pagehide", function () {
      if (ov.classList.contains("is-open")) close();
    });
    window.addEventListener("pageshow", function (e) {
      if (e && e.persisted) close(); // no-ops when already closed
    });

    /* Safety: if the cart drawer takes over the page, get out of the way.
       (theme:cart:open is real — dispatched by openCartDrawer with
       bubbles:true. There is deliberately NO theme:quick-view:open listener:
       that event name is dispatched nowhere in this theme, so it was dead code
       masquerading as a guarantee. The quick-view case is covered by the
       independent documentElement overflow restore in unlockScroll().) */
    document.addEventListener("theme:cart:open", close);

    /* Mark the gallery images focusable now, and again whenever the theme
       editor or a section reload replaces the markup. */
    markEligible(document);
    document.addEventListener("shopify:section:load", function (e) {
      markEligible((e && e.target) || document);
    });
  }

  if (document.body) build();
  else document.addEventListener("DOMContentLoaded", build);
})();
